import {
  modelCatalogueOf,
  panoVariantsOf,
  servedWorldsOf,
  validateLesson,
  type Lesson,
  type ModelCatalogue,
  type PanoVariants,
  type ServedWorld,
} from '@vrlearning/lesson-schema';
import { catalogueOfServed } from './api/served-catalogue.js';

/**
 * Test-driving a library item (ADR-048; contract L8's TestDriveAnswer): the console opens this app at
 * `?testDrive=<ticket>`; the ticket -- once, two minutes -- fetches the item and every view of it, each
 * a small lesson the API built. No pairing, no session, nothing recorded; `bootTestDrive` in main.ts
 * plays a view and adds the figure and the test panel.
 */
export const TEST_DRIVE_TEXT = {
  loading: 'De testrit wordt geladen…',
  gone: 'Deze testrit is verlopen of al gebruikt. Vraag in de console een nieuwe aan met "Bekijk op de Quest".',
  failed: 'De testrit kon niet worden geladen.',
  ended: {
    title: 'Klaar met deze weergave',
    body: 'Kies "Opnieuw" om deze weergave nog eens te bekijken, of sluit dit tabblad.',
  },
  again: 'Opnieuw',
} as const;

export type TestDriveView = {
  readonly id: string;
  readonly label: string;
  readonly lesson: Lesson;
  readonly figure: { readonly pos: readonly [number, number, number] } | null;
  readonly scalable: 'item' | null;
  /**
   * A world package's views (ADR-049): `pakket` builds the served package of the slug (its current
   * version), `nieuw` its candidate version (world versions), `code` the headset's own code world of
   * it; null for every other kind.
   */
  readonly world: 'pakket' | 'nieuw' | 'code' | null;
};

export type TestDriveAnswer = {
  readonly item: {
    readonly id: string;
    readonly kind: 'object' | 'plek360' | 'wereld';
    readonly name: string;
    readonly state: string;
  };
  readonly views: readonly TestDriveView[];
  readonly media: Readonly<Record<string, string>>;
  readonly variants: Readonly<PanoVariants>;
  readonly models: ModelCatalogue;
  /** The served world package of a `wereld` item, by slug (contract W8); `{}` for the other kinds. */
  readonly worlds: Readonly<Record<string, ServedWorld>>;
  /** A published world's new version, by slug, for the `nieuw` view; `{}` without one. */
  readonly candidate: Readonly<Record<string, ServedWorld>>;
};

export type TestDriveOutcome =
  | { kind: 'ready'; answer: TestDriveAnswer }
  | { kind: 'gone' }
  | { kind: 'failed'; detail: string };

/** What the address says this tab is for: a ticket to redeem, or a tab that redeemed one and was reloaded. */
export type TestDriveStart = { kind: 'ticket'; ticket: string } | { kind: 'spent' };

/**
 * Marks the history entry a ticket was read from, as the play ticket does (play.ts): the entry's state
 * outlives a reload while the address no longer says anything, and without the mark a reloaded tab
 * would boot as a headset -- the pairing screen on a laptop, a check-in on a paired Quest.
 */
const SPENT = 'vrlearningTestDrive';

/** Reads `?testDrive=<ticket>` and takes it off the address at once (a used ticket is worthless). */
export function takeTestDriveRequest(
  place: { href: string } | undefined = globalThis.location,
  history: Pick<History, 'state' | 'replaceState'> | undefined = globalThis.history,
): TestDriveStart | undefined {
  let url: URL | undefined;
  try {
    url = new URL(place?.href ?? '');
  } catch {
    url = undefined;
  }
  const ticket = url?.searchParams.get('testDrive');
  if (url && ticket) {
    // Replaced, not pushed: neither the address bar nor the tab's history keeps it.
    history?.replaceState({ [SPENT]: true }, '', url.pathname);
    return { kind: 'ticket', ticket };
  }
  const state: unknown = history?.state;
  if (
    typeof state === 'object' &&
    state !== null &&
    (state as Record<string, unknown>)[SPENT] === true
  ) {
    return { kind: 'spent' };
  }
  return undefined;
}

const textOf = (value: unknown): string => (typeof value === 'string' ? value : '');

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

function figureOf(value: unknown): TestDriveView['figure'] {
  if (!isRecord(value) || !Array.isArray(value['pos']) || value['pos'].length !== 3) return null;
  const pos = (value['pos'] as unknown[]).map(Number);
  return pos.every(Number.isFinite) ? { pos: [pos[0]!, pos[1]!, pos[2]!] } : null;
}

export async function fetchTestDrive(
  ticket: string,
  fetchImpl: typeof fetch = (input, init) => fetch(input, init),
): Promise<TestDriveOutcome> {
  let response: Response;
  try {
    // Uncached: the answer is good once, and a stored copy would replay it.
    response = await fetchImpl(`/v1/test-drive/${encodeURIComponent(ticket)}`, {
      cache: 'no-store',
    });
  } catch {
    return { kind: 'failed', detail: 'geen verbinding met de server' };
  }
  if (response.status === 404) return { kind: 'gone' };
  if (!response.ok) return { kind: 'failed', detail: `fout ${response.status}` };
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { kind: 'failed', detail: 'onleesbaar antwoord' };
  }
  if (!isRecord(body) || !isRecord(body['item']) || !Array.isArray(body['views'])) {
    return { kind: 'failed', detail: 'onvolledig antwoord' };
  }
  const worlds = servedWorldsOf(body['worlds']);
  const candidate = servedWorldsOf(body['candidate']);
  const views: TestDriveView[] = [];
  for (const raw of body['views'] as unknown[]) {
    if (!isRecord(raw) || typeof raw['id'] !== 'string' || typeof raw['label'] !== 'string') {
      return { kind: 'failed', detail: 'onvolledig antwoord' };
    }
    // Judged by the worlds of its view: the package, its new version, or the headset's own code worlds.
    const kind = raw['world'];
    const known = kind === 'code' ? {} : kind === 'nieuw' ? { ...worlds, ...candidate } : worlds;
    const result = validateLesson(raw['lesson'], { worlds: catalogueOfServed(known) });
    if (!result.valid) {
      const first = result.errors[0];
      return {
        kind: 'failed',
        detail: `les ${raw['id']} is ongeldig: ${first?.path ?? ''} ${first?.message ?? ''}`.trim(),
      };
    }
    views.push({
      id: raw['id'],
      label: raw['label'],
      lesson: result.lesson,
      figure: figureOf(raw['figure']),
      scalable: raw['scalable'] === 'item' ? 'item' : null,
      world:
        raw['world'] === 'pakket' || raw['world'] === 'nieuw' || raw['world'] === 'code'
          ? raw['world']
          : null,
    });
  }
  if (views.length === 0) return { kind: 'failed', detail: 'geen weergaven' };
  const item = body['item'];
  return {
    kind: 'ready',
    answer: {
      item: {
        id: textOf(item['id']),
        kind: item['kind'] === 'plek360' || item['kind'] === 'wereld' ? item['kind'] : 'object',
        name: textOf(item['name']),
        state: textOf(item['state']),
      },
      views,
      media: isRecord(body['media'])
        ? Object.fromEntries(
            Object.entries(body['media']).filter(
              (entry): entry is [string, string] => typeof entry[1] === 'string',
            ),
          )
        : {},
      variants: panoVariantsOf(body['variants']),
      models: modelCatalogueOf(body['models']),
      worlds,
      candidate,
    },
  };
}
